/**
 * A2 legacy GET /patients/edit/{id} query params (patient_information_id, health_insurance_card_id, medical_aid_id, type).
 * Used to mirror which history row / “new row” draft each section is in.
 */

import type { PatientFormData } from '@pages/main/patient/types';
import { computeBulkHistoryBdDraftAdjustments } from '@pages/main/patient/utils/bulkHistoryBdTimeframe';

export const PATIENT_EDIT_HISTORY_SEARCH = {
  patient_information_id: 'patient_information_id',
  health_insurance_card_id: 'health_insurance_card_id',
  medical_aid_id: 'medical_aid_id',
  type: 'type',
  bulk_qualification_date: 'bulk_qualification_date',
} as const;

export type PatientEditHistoryIdParam = 'absent' | 'empty' | number;

export function parsePatientEditHistoryIdParam(
  sp: URLSearchParams,
  key: string
): PatientEditHistoryIdParam {
  if (!sp.has(key)) return 'absent';
  const raw = sp.get(key);
  if (raw == null || String(raw).trim() === '') return 'empty';
  const n = parseInt(String(raw).trim(), 10);
  return Number.isFinite(n) ? n : 'empty';
}

export function hasPatientEditHistorySearchParams(sp: URLSearchParams): boolean {
  return (
    sp.has(PATIENT_EDIT_HISTORY_SEARCH.patient_information_id) ||
    sp.has(PATIENT_EDIT_HISTORY_SEARCH.health_insurance_card_id) ||
    sp.has(PATIENT_EDIT_HISTORY_SEARCH.medical_aid_id) ||
    Boolean(sp.get(PATIENT_EDIT_HISTORY_SEARCH.type)?.trim())
  );
}

/** A2 dataInit-style defaults for URL (empty string = key present, new draft). */
export function buildPatientEditHistorySearchParams(opts: {
  patientInformationId?: number | null;
  healthInsuranceCardId?: number | null;
  medicalAidId?: number | null;
  /** A2: all_change on まとめて (retun_modal 7) */
  type?: 'all_change' | 'one';
  /** Modal 保険証資格取得日. Used to preserve bulk-history draft fields across URL re-init. */
  bulkQualificationDate?: string;
}): URLSearchParams {
  const sp = new URLSearchParams();
  sp.set(
    PATIENT_EDIT_HISTORY_SEARCH.patient_information_id,
    opts.patientInformationId != null ? String(opts.patientInformationId) : ''
  );
  sp.set(
    PATIENT_EDIT_HISTORY_SEARCH.health_insurance_card_id,
    opts.healthInsuranceCardId != null ? String(opts.healthInsuranceCardId) : ''
  );
  sp.set(
    PATIENT_EDIT_HISTORY_SEARCH.medical_aid_id,
    opts.medicalAidId != null ? String(opts.medicalAidId) : ''
  );
  if (opts.type) {
    sp.set(PATIENT_EDIT_HISTORY_SEARCH.type, opts.type);
  }
  if (opts.bulkQualificationDate) {
    sp.set(PATIENT_EDIT_HISTORY_SEARCH.bulk_qualification_date, opts.bulkQualificationDate);
  }
  return sp;
}

function addDaysIso(yyyyMmDd: string, deltaDays: number): string | undefined {
  if (!yyyyMmDd || !/^\d{4}-\d{2}-\d{2}$/.test(yyyyMmDd)) return undefined;
  const d = new Date(yyyyMmDd);
  if (Number.isNaN(d.getTime())) return undefined;
  d.setDate(d.getDate() + deltaDays);
  return d.toISOString().split('T')[0];
}

/**
 * Apply A2 PatientController::dataInit query semantics to a loaded {@link PatientFormData} snapshot.
 * Caller should run their normal normalize (e.g. single-current flags) after, if needed.
 */
export function applyLegacyPatientEditSearchParams(
  raw: PatientFormData,
  sp: URLSearchParams
): PatientFormData {
  const data = JSON.parse(JSON.stringify(raw)) as PatientFormData;

  const piKey = PATIENT_EDIT_HISTORY_SEARCH.patient_information_id;
  const hicKey = PATIENT_EDIT_HISTORY_SEARCH.health_insurance_card_id;
  const maKey = PATIENT_EDIT_HISTORY_SEARCH.medical_aid_id;

  const pi = parsePatientEditHistoryIdParam(sp, piKey);
  const hic = parsePatientEditHistoryIdParam(sp, hicKey);
  const ma = parsePatientEditHistoryIdParam(sp, maKey);
  const bulkQualificationDate = sp
    .get(PATIENT_EDIT_HISTORY_SEARCH.bulk_qualification_date)
    ?.trim()
    .slice(0, 10);
  const bulkDraft =
    sp.get(PATIENT_EDIT_HISTORY_SEARCH.type)?.trim() === 'all_change' && bulkQualificationDate
      ? computeBulkHistoryBdDraftAdjustments(data, bulkQualificationDate)
      : null;

  const sourcePi =
    data.current_patient_information ||
    data.patient_informations?.find(i => i.is_current) ||
    data.patient_informations?.[0];
  const sourceHic =
    data.health_insurance_cards?.find(c => c.is_current) || data.health_insurance_cards?.[0];
  const sourceMa = data.medical_aids?.find(a => a.is_current) || data.medical_aids?.[0];

  let baseAvailableToPlusOne: string | undefined;

  if (pi === 'empty') {
    if (sourcePi) {
      const copy = { ...sourcePi } as typeof sourcePi;
      if (copy.available_to) {
        baseAvailableToPlusOne = addDaysIso(String(copy.available_to).slice(0, 10), 1);
      }
      delete copy.id;
      delete (copy as { patient_information_id?: number | null }).patient_information_id;
      copy.available_from = bulkQualificationDate || undefined;
      copy.available_to = bulkDraft ? bulkDraft.patientInformationAvailableTo : undefined;
      copy.is_current = true;
      data.current_patient_information = copy;
    }
  } else if (typeof pi === 'number') {
    const row = data.patient_informations?.find(i => i.id === pi);
    if (row) {
      data.patient_informations = (data.patient_informations || []).map(i => ({
        ...i,
        is_current: i.id === pi,
      }));
      data.current_patient_information = { ...row, is_current: true };
    }
  }

  if (hic === 'empty') {
    if (sourceHic) {
      const copy = { ...sourceHic } as typeof sourceHic;
      delete copy.id;
      delete (copy as { health_insurance_card_id?: number | null }).health_insurance_card_id;
      copy.qualification_date = bulkDraft?.healthInsuranceQualificationDate || baseAvailableToPlusOne || '';
      copy.expiration_date = bulkDraft?.healthInsuranceExpirationDate || '';
      copy.is_current = true;
      data.health_insurance_cards = [
        copy,
        ...(data.health_insurance_cards || []).map(c => ({ ...c, is_current: false })),
      ];
    }
  } else if (typeof hic === 'number') {
    const row = data.health_insurance_cards?.find(c => c.id === hic);
    if (row) {
      data.health_insurance_cards = (data.health_insurance_cards || []).map(c => ({
        ...c,
        is_current: c.id === hic,
      }));
    }
  }

  if (ma === 'empty') {
    if (sourceMa) {
      const copy = { ...sourceMa } as typeof sourceMa;
      delete copy.id;
      delete (copy as { medical_aid_id?: number | null }).medical_aid_id;
      copy.application_start_date = bulkDraft?.medicalAidApplicationStartDate || '';
      copy.application_end_date = bulkDraft?.medicalAidApplicationEndDate || '';
      copy.is_current = true;
      data.medical_aids = [
        copy,
        ...(data.medical_aids || []).map(a => ({ ...a, is_current: false })),
      ];
    }
  } else if (typeof ma === 'number') {
    const row = data.medical_aids?.find(a => a.id === ma);
    if (row) {
      data.medical_aids = (data.medical_aids || []).map(a => ({
        ...a,
        is_current: a.id === ma,
      }));
    }
  }

  return data;
}
